/**
 * 가로 마키/드래그 스크롤 순수 계산 (Phaser 비의존 — 단위테스트 대상).
 *
 * 좌표계: offset = 텍스트가 기준점(baseX)에서 밀린 px. LTR 기준 0=좌정렬, 음수=좌로 밀려
 * 오른쪽 끝이 보임. RTL 부호/정렬은 소비 컴포넌트(MarqueeText)가 흡수하고 본 모듈은 방향 무관.
 */

export interface MarqueeMetrics {
  /** 텍스트 전체 폭(px). */
  readonly textWidth: number;
  /** 클립되는 뷰포트 폭(px). */
  readonly viewWidth: number;
}

/** 마키 진행 상태 — 컴포넌트가 보유하고 매 tick advanceMarquee로 갱신. */
export interface MarqueeState {
  /** 현재 offset(px, ≤0). */
  readonly offset: number;
  /** 진행 방향(-1: 좌로 밀기 / +1: 우로 복귀). */
  readonly dir: 1 | -1;
  /** 경계에서 멈춰 있는 잔여 시간(ms). >0이면 이동 정지. */
  readonly holdRemainMs: number;
}

/** 마키 속도/멈춤 설정. */
export interface MarqueeConfig {
  /** 자동 흐름 속도(px/초). */
  readonly speedPxPerSec: number;
  /** 경계 도달 후 멈춤 시간(ms). */
  readonly holdMs: number;
}

/** 텍스트가 뷰포트를 넘치는가(같으면 정지). */
export function isOverflowing(m: MarqueeMetrics): boolean {
  return m.textWidth > m.viewWidth;
}

/** offset 허용 범위. 넘치면 min=viewW−textW(음수)·max=0, 비넘침이면 min=max=0(정지). */
export function marqueeOffsetBounds(m: MarqueeMetrics): { min: number; max: number } {
  if (!isOverflowing(m)) return { min: 0, max: 0 };
  return { min: m.viewWidth - m.textWidth, max: 0 };
}

/** 드래그 delta를 적용한 offset을 bounds로 클램프. 우로 끌면(+) max(0), 좌로 끌면(−) min. */
export function clampDragOffset(
  dragStartOffset: number,
  deltaX: number,
  m: MarqueeMetrics,
): number {
  const { min, max } = marqueeOffsetBounds(m);
  const raw = dragStartOffset + deltaX;
  if (raw < min) return min;
  if (raw > max) return max;
  return raw;
}

/** 스크롤바 썸 배치 결과(트랙 내 시각 좌표). */
export interface ScrollbarThumb {
  /** 썸 좌상단 x(px). */
  readonly x: number;
  /** 썸 폭(px). */
  readonly width: number;
  /** 넘칠 때만 표시. */
  readonly visible: boolean;
}

/**
 * 가로 스크롤바 썸 위치/폭 계산. 썸 폭 = 보이는 비율(viewW/textW)에 비례(minThumbWidth 하한).
 * 진행률 = offset/min(0=좌끝, 1=우끝). RTL이면 트랙 내 좌우 미러. 비넘침이면 visible=false.
 */
export function computeScrollbarThumb(
  m: MarqueeMetrics,
  offset: number,
  trackX: number,
  rtl: boolean,
  minThumbWidth: number,
): ScrollbarThumb {
  if (!isOverflowing(m)) return { x: trackX, width: m.viewWidth, visible: false };
  const ratio = m.viewWidth / m.textWidth;
  const width = Math.max(minThumbWidth, m.viewWidth * ratio);
  const travel = Math.max(0, m.viewWidth - width);
  const { min } = marqueeOffsetBounds(m);
  const progress = min === 0 ? 0 : offset / min; // offset∈[min,0] → [1,0]; offset/min → [1..0]? min<0
  const p = Math.min(1, Math.max(0, progress));
  const x = trackX + travel * (rtl ? 1 - p : p);
  return { x, width, visible: true };
}

/**
 * 시간 진행으로 마키 offset 갱신(bounce: 좌끝↔우끝 왕복 + 경계 hold).
 * 비넘침이면 항상 offset 0 정지. holdRemainMs>0이면 이동 없이 카운트다운만.
 */
export function advanceMarquee(
  state: MarqueeState,
  m: MarqueeMetrics,
  dtMs: number,
  cfg: MarqueeConfig,
): MarqueeState {
  const { min, max } = marqueeOffsetBounds(m);
  if (min === max) return { offset: 0, dir: state.dir, holdRemainMs: 0 };
  if (state.holdRemainMs > 0) {
    return { ...state, holdRemainMs: Math.max(0, state.holdRemainMs - dtMs) };
  }
  const delta = (state.dir * cfg.speedPxPerSec * dtMs) / 1000;
  const next = state.offset + delta;
  if (state.dir === -1 && next <= min) {
    return { offset: min, dir: 1, holdRemainMs: cfg.holdMs };
  }
  if (state.dir === 1 && next >= max) {
    return { offset: max, dir: -1, holdRemainMs: cfg.holdMs };
  }
  return { offset: next, dir: state.dir, holdRemainMs: 0 };
}
